"use client";
import { RadioInput } from "@cmp-src/form/RadioInput";
import { Label } from "@cmp-src/primitives/label";
import { TextInput } from "@cmp-src/form/TextInput";
import { cn } from "@gettactic/helpers/src/css/cn";
import { useAuthenticated } from "@gettactic/hooks";
import { addDays, startOfDay } from "date-fns";
import { motion } from "framer-motion";
import { ChevronRightIcon, XIcon } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import type { SelectItem } from "@cmp-src/form/UnifiedSelect";
import { UnifiedSelectInput } from "@cmp-src/form/UnifiedSelectInput";
import { type Frequency, RRule, type Weekday } from "rrule";
import { canScheduleOthers } from "@gettactic/helpers";
import { Button } from "@cmp-src/primitives/button";
import { CalendarInput } from "@cmp-src/form/calendar/CalendarInput/CalendarInput";
import type { DateValue } from "@cmp-src/calendar/types";

const validPeriodicityOptions: SelectItem<{
  freq: AdvancedFormState["freq"];
}>[] = [
  { id: String(RRule.DAILY), name: "Day", data: { freq: RRule.DAILY } },
  { id: String(RRule.WEEKLY), name: "Week", data: { freq: RRule.WEEKLY } },
  { id: String(RRule.MONTHLY), name: "Month", data: { freq: RRule.MONTHLY } },
  { id: String(RRule.YEARLY), name: "Year", data: { freq: RRule.YEARLY } }
];
//

const repeatOnDays: { label: string; value: Weekday }[] = [
  { label: "Sunday", value: RRule.SU },
  { label: "Monday", value: RRule.MO },
  { label: "Tuesday", value: RRule.TU },
  { label: "Wednesday", value: RRule.WE },
  { label: "Thursday", value: RRule.TH },
  { label: "Friday", value: RRule.FR },
  { label: "Saturday", value: RRule.SA }
];

type Props = {
  setAdvanced: React.Dispatch<React.SetStateAction<boolean>>;
  state: AdvancedFormState;
  setState: (x: AdvancedFormState) => void;
  hideWeekends?: boolean;
  minUntilDate: Date;
  isCompact?: boolean;
  showNeverOption?: boolean;
  maxDateAllowed?: Date;
  isCustom?: boolean;
  advanced?: boolean;
  wasNeverEnding?: boolean;
};

export type AdvancedFormState = {
  interval: number;
  freq: Frequency;
  byweekday: Weekday[];
  until: Date | null;
};

export function ReserveFieldRecurringAdvancedForm({
  setAdvanced,
  state,
  setState,
  hideWeekends,
  minUntilDate,
  isCompact = false,
  showNeverOption = true,
  maxDateAllowed,
  isCustom = false,
  advanced = false,
  wasNeverEnding = false
}: Props) {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const isAdmin = canScheduleOthers(authenticatedUser);

  const [untilDate, setUntilDate] = useState(
    state.until ?? (maxDateAllowed || addDays(minUntilDate, 30))
  );

  useEffect(() => {
    if (state.until) {
      setUntilDate(state.until);
    } else if (maxDateAllowed && !state.until) {
      // If no until date is set and we have a maxDateAllowed, use it as default
      setUntilDate(maxDateAllowed);
    }
  }, [state.until, maxDateAllowed]);

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="relative flex flex-col overflow-hidden rounded-lg border border-neutral-100 bg-neutral-50 p-4"
    >
      {/* Ends on section - always visible when recurring is selected */}
      <div className={advanced ? "order-3" : ""}>
        <div className="flex items-center justify-between">
          <Label className={cn("w-auto", !isCompact && "basis-1/4")}>Ends</Label>
          {!advanced && isCustom && (
            <Button
              className="flex items-center font-inter text-primary text-sm hover:text-primary-dark"
              variant="ghost"
              onClick={(ev) => {
                ev.preventDefault();
                setAdvanced(true);
              }}
            >
              Options <ChevronRightIcon className="h-4 w-4 rotate-90" />
            </Button>
          )}
        </div>
        <div className="flex flex-col">
          {showNeverOption && (
            <RadioInput
              label="Never"
              radioPosition="left"
              checked={state.until === null}
              onChange={() => {
                setState({ ...state, until: null });
              }}
            />
          )}
          <div className={cn("flex items-center justify-between")}>
            {showNeverOption && (
              <RadioInput
                label="On"
                radioPosition="left"
                checked={state.until !== null}
                onChange={() => {
                  setState({ ...state, until: untilDate });
                }}
              />
            )}
            <div
              className={cn(
                "flex grow basis-3/4",
                isCompact ? "ml-4" : showNeverOption ? "ml-4" : ""
              )}
            >
              <div
                className={cn(
                  "w-full",
                  !state.until && "opacity-50"
                )}
              >
                <CalendarInput
                  mode="single"
                  variant="outlined"
                  inputClassName="shadow-input md:rounded-md"
                        selected={untilDate}
                  selectedDate={untilDate}
                  onSelect={(date: DateValue | undefined) => {
                    if (date instanceof Date) {
                      setState({ ...state, until: date });
                    }
                  }}
                  setValueOnApply={false}
                  disabled={(day: Date) => {
                    const start = startOfDay(minUntilDate);
                    const dayStart = startOfDay(day);
                    if (dayStart < start) return true;
                    if (maxDateAllowed && !isAdmin) {
                      const end = startOfDay(maxDateAllowed);
                      if (dayStart > end) return true;
                    }
                    return false;
                  }}
                />
              </div>
              {/* Show "Currently ends: Never" text when appropriate */}
              {wasNeverEnding && !showNeverOption && (
                <div className="mb-2 ml-3 text-neutral-600 text-sm">
                  Currently ends: Never
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Advanced options - only show when advanced is true and custom recurrence is selected */}
      {advanced && isCustom && (
        <>
          <div className="order-1 flex items-center justify-between">
            <span className="font-medium text-neutral-800 text-sm">Options</span>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              onClick={(ev) => {
                ev.preventDefault();
                setAdvanced(false);
              }}
            >
              <XIcon className="size-4" />
            </button>
          </div>
          <div
            className={cn(
              "mt-4 flex order-2",
              isCompact ? "flex-col items-start" : "items-center"
            )}
          >
            <Label
              className={cn("w-auto", isCompact ? "mb-1.5" : "basis-1/4")}
            >
              Repeat every
            </Label>
            <div
              className={cn(
                "flex items-center gap-2",
                isCompact ? "w-full" : "gap-3"
              )}
            >
              <div className={cn(isCompact ? "flex-1" : "basis-1/4")}>
                <div>
                  <TextInput
                    onChange={(ev) => {
                      if (ev.currentTarget.valueAsNumber < 0) {
                        return;
                      }
                      setState({
                        ...state,
                        interval: ev.currentTarget.valueAsNumber
                      });
                    }}
                    value={state.interval.toString()}
                    type="number"
                  />
                </div>
              </div>

              <div className={cn(isCompact ? "flex-1" : "basis-1/4")}>
                <UnifiedSelectInput
                  items={validPeriodicityOptions}
                  value={
                    validPeriodicityOptions.find(
                      (x) => x.data?.freq === state.freq
                    )?.id
                  }
                  onChangeValue={(id) => {
                    const item = validPeriodicityOptions.find(
                      (x) => x.id === id
                    );
                    if (item?.data?.freq !== undefined) {
                      setState({
                        ...state,
                        freq: item.data.freq,
                        byweekday:
                          item.data.freq === RRule.DAILY
                            ? state.byweekday
                            : []
                      });
                    }
                  }}
                />
              </div>
            </div>
          </div>
          {state.freq === RRule.DAILY && (
            <div
              className={cn(
                "mt-2 flex order-2",
                isCompact ? "flex-col items-start mt-4" : "items-center"
              )}
            >
              <Label
                className={cn("w-auto", isCompact ? "mb-1.5" : "basis-1/4")}
              >
                Repeat on
              </Label>
              <div
                className={cn(
                  "flex items-center",
                  isCompact ? "gap-1.5" : "gap-3 space-x-1"
                )}
              >
                {repeatOnDays
                  .filter((day) =>
                    hideWeekends
                      ? !(day.value === RRule.SU || day.value === RRule.SA)
                      : true
                  )
                  .map((day) => {
                    const idx = state.byweekday.findIndex(
                      (x) => x.weekday === day.value.weekday
                    );
                    return (
                      // biome-ignore lint/a11y/useKeyWithClickEvents: <keep the div for the click event>
                      <div
                        key={day.value.toString()}
                        onClick={((idx, value) => () => {
                          const byweekday: Weekday[] = [...state.byweekday];
                          if (idx > -1) {
                            byweekday.splice(idx, 1);
                          } else {
                            byweekday.push(value);
                          }
                          setState({ ...state, byweekday: byweekday });
                        })(idx, day.value)}
                        className={cn(
                          "font-inter flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border text-center text-[0.9rem] capitalize",
                          idx > -1
                            ? "bg-primary text-white "
                            : "border-neutral-200 text-secondary-foreground"
                        )}
                        title={day.label}
                      >
                        {day.label[0]}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </>
      )}
    </motion.div>
  );
}

